import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type AwardCampaignView, type AwardGrantView, type AwardPrizeTypeView } from '@vello/api-client';
import { formatMoney } from '@vello/format';
import { Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, ListTable, MoneyInput, Notice, PageHeader, Panel, Select, TextInput, type Column, FormGrid, SectionTitle, Text, InlineRow, Measure, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';
import {
  maySelfApprove,
  awardAuthority,
  awardDecideBlock,
  awardStatusTone,
  budgetUsed,
  formatMarketInstant,
  marketTimeZone,
  zonedLocalToUtc,
} from '../../src/index';
import { useAdmin } from '../session';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

interface Draft {
  readonly name: string;
  readonly prizeType: AwardPrizeTypeView;
  readonly amountMinor: bigint | undefined;
  readonly bps: string;
  readonly completed: string;
  readonly accepted: string;
  readonly from: string;
  readonly to: string;
  readonly groupId: string;
  readonly perDriverMinor: bigint | undefined;
  readonly budgetMinor: bigint | undefined;
}

const EMPTY: Draft = { name: '', prizeType: 'FIXED', amountMinor: undefined, bps: '', completed: '', accepted: '', from: '', to: '', groupId: '', perDriverMinor: undefined, budgetMinor: undefined };

export function AwardsScreen(): ReactElement {
  const { runtime, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = awardAuthority(grants);
  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [campaigns, setCampaigns] = useState<readonly AwardCampaignView[] | undefined>(undefined);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [paid, setPaid] = useState<{ readonly campaign: AwardCampaignView; readonly grants: readonly AwardGrantView[] } | undefined>(undefined);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [reasons, setReasons] = useState<Readonly<Record<string, string>>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.broadcast', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setCampaigns(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.awards.list(market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setCampaigns(res.value.campaigns);
      setCurrency(res.value.currency ?? undefined);
      setError(undefined);
    } else setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
  }, [runtime, market, refuse]);

  useEffect(() => {
    setCampaigns(undefined);
    setPaid(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const update = (patch: Partial<Draft>): void => setDraft((d) => ({ ...d, ...patch }));
  const money = (m: { readonly amountMinor: string; readonly currency: string }): string => formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });

  const propose = async (): Promise<void> => {
    if (market === undefined) return;
    const tz = timeZone ?? 'UTC';
    const from = zonedLocalToUtc(draft.from, tz);
    const to = zonedLocalToUtc(draft.to, tz);
    if (from === undefined || to === undefined) {
      setNotice({ tone: 'danger', text: t('aw.err.window') });
      return;
    }
    const completed = Number(draft.completed);
    const accepted = Number(draft.accepted);
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.awards.propose({
      marketId: market,
      name: draft.name.trim(),
      prizeType: draft.prizeType,
      ...(draft.prizeType === 'FIXED' ? { prizeAmountMinor: (draft.amountMinor ?? 0n).toString() } : { prizeBps: Number(draft.bps) }),
      targetCompleted: Number.isInteger(completed) ? completed : 0,
      ...(draft.accepted.trim() !== '' ? { targetAccepted: Number.isInteger(accepted) ? accepted : 0 } : {}),
      from,
      to,
      ...(draft.groupId.trim() !== '' ? { groupId: draft.groupId.trim() } : {}),
      perDriverCapMinor: (draft.perDriverMinor ?? 0n).toString(),
      totalCapMinor: (draft.budgetMinor ?? 0n).toString(),
    });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('aw.done.proposed') });
      setDraft(EMPTY);
      await load();
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const decide = async (c: AwardCampaignView, approve: boolean): Promise<void> => {
    if (market === undefined) return;
    const reason = (reasons[c.campaignId] ?? '').trim();
    if (!approve && reason === '') {
      setNotice({ tone: 'danger', text: t('aw.err.reason') });
      return;
    }
    setBusy(true);
    const res = await runtime.awards.decide(market, c.campaignId, { approve, ...(approve ? {} : { reason }) });
    setBusy(false);
    if (res.ok) setNotice({ tone: 'success', text: t(approve ? 'aw.done.approved' : 'aw.done.rejected') });
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const end = async (c: AwardCampaignView): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    const res = await runtime.awards.end(market, c.campaignId);
    setBusy(false);
    if (res.ok) setNotice({ tone: 'success', text: t('aw.done.ended') });
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const openGrants = async (c: AwardCampaignView): Promise<void> => {
    if (market === undefined) return;
    const res = await runtime.awards.get(market, c.campaignId);
    if (res.ok) setPaid(res.value);
    else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
  };

  const prizeText = (c: AwardCampaignView): string =>
    c.prize.type === 'FIXED' && c.prize.amount !== undefined ? t('aw.prize.fixed', { amount: money(c.prize.amount) }) : t('aw.prize.pct', { percent: ((c.prize.basisPoints ?? 0) / 100).toString(), cap: money(c.prize.perDriverCap) });

  const columns: readonly Column<AwardCampaignView>[] = [
    { key: 'name', header: t('aw.col.name'), width: 'minmax(190px, 1.5fr)', render: (c) => <span><strong>{c.name}</strong> <IdRef value={c.campaignId} /></span> },
    { key: 'prize', header: t('aw.col.prize'), width: 'minmax(190px, 1.3fr)', render: (c) => <span className="tnum"><Badge tone="neutral">{v('incentives.award_prize_type', c.prize.type)}</Badge> {prizeText(c)}</span> },
    { key: 'target', header: t('aw.col.target'), width: 'minmax(170px, 1.2fr)', render: (c) => <span className="tnum">{c.target.accepted === null ? t('aw.target.line', { completed: c.target.completed }) : t('aw.target.both', { completed: c.target.completed, accepted: c.target.accepted })}</span> },
    { key: 'window', header: t('aw.col.window'), width: 'minmax(220px, 1.5fr)', render: (c) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(c.window.from, timeZone, lang)} → {formatMarketInstant(c.window.to, timeZone, lang)}</Text> },
    { key: 'spent', header: t('aw.col.spent'), width: 'minmax(190px, 1.2fr)', render: (c) => <span className="tnum">{money(c.spent)} / {money(c.totalCap)} <Text role="caption" tone="secondary">({Math.round(budgetUsed(c) * 100)}%)</Text>
        {c.funding !== undefined && c.funding !== null ? (
          <>
            <br /><Text role="caption" tone="secondary">{t('aw.funding.line', { cap: money(c.funding.cap), drawn: money(c.funding.drawn), remaining: money(c.funding.remaining) })}</Text>
            {c.funding.unpaidCount > 0 && <><br /><Text role="caption" tone="danger">{t('aw.funding.unpaid', { count: c.funding.unpaidCount, amount: money(c.funding.unpaid) })}</Text></>}
          </>
        ) : null}</span> },
    { key: 'status', header: t('aw.col.status'), width: 'minmax(150px, 1fr)', render: (c) => <Badge tone={awardStatusTone(c.status)}>{v('award.status', c.status)}</Badge> },
  ];

  const rowActions = (c: AwardCampaignView): ReactElement => {
    const block = awardDecideBlock(c, me?.id, authority.mayManage, selfApproval);
    return (
      <InlineRow gap={2} align="center" wrap>
        {c.status === 'PROPOSED' && block === undefined && (
          <>
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void decide(c, true)}>{t('aw.action.approve')}</Button>
            <TextInput aria-label={t('aw.field.reason')} placeholder={t('aw.field.reason')} value={reasons[c.campaignId] ?? ''} onChange={(e) => setReasons({ ...reasons, [c.campaignId]: e.target.value })} className="vello-admin-a-narrow-md" />
            <Button variant="danger" size="sm" disabled={busy} onClick={() => void decide(c, false)}>{t('aw.action.reject')}</Button>
          </>
        )}
        {c.status === 'PROPOSED' && block === 'OWN_PROPOSAL' && <Text role="caption" tone="secondary">{t('aw.own')}</Text>}
        {c.status === 'ACTIVE' && authority.mayManage && <Button variant="danger" size="sm" disabled={busy} onClick={() => void end(c)}>{t('aw.action.end')}</Button>}
      </InlineRow>
    );
  };

  return (
    <>
      <PageHeader title={t('aw.title')} subtitle={t('aw.subtitle')} />

      {error !== undefined && <div><Notice tone="danger">{error}</Notice></div>}
      {notice !== undefined && <div><Notice tone={notice.tone}>{notice.text}</Notice></div>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          {authority.mayManage && (
            <Panel>
              <SectionTitle>{t('aw.section.new')}</SectionTitle>
              {currency === undefined ? (
                <Notice tone="warning">{t('aw.currencyUnknown')}</Notice>
              ) : (
                <Measure size="lg" gap={4}>
                  <Field label={t('aw.field.name')} htmlFor="aw-name">
                    <TextInput id="aw-name" value={draft.name} maxLength={80} onChange={(e) => update({ name: e.target.value })} />
                  </Field>
                  <FormGrid>
                    <Field label={t('aw.field.prizeType')} htmlFor="aw-prize-type">
                      <Select id="aw-prize-type" value={draft.prizeType} onChange={(e) => update({ prizeType: e.target.value as AwardPrizeTypeView })}>
                        {(['FIXED', 'PCT_TOTAL', 'PCT_REPASSE'] as const).map((p) => <option key={p} value={p}>{v('incentives.award_prize_type', p)}</option>)}
                      </Select>
                    </Field>
                    {draft.prizeType === 'FIXED' ? (
                      <MoneyInput id="aw-amount" label={t('aw.field.amount')} currency={currency} locale={lang} valueMinor={draft.amountMinor} onChangeMinor={(v) => update({ amountMinor: v })} issueText={(i) => v('format.money_input_issue', i)} />
                    ) : (
                      <Field label={t('aw.field.bps')} htmlFor="aw-bps">
                        <TextInput id="aw-bps" className="tnum" inputMode="numeric" value={draft.bps} onChange={(e) => update({ bps: e.target.value })} />
                      </Field>
                    )}
                  </FormGrid>
                  <FormGrid>
                    <Field label={t('aw.field.targetCompleted')} htmlFor="aw-completed">
                      <TextInput id="aw-completed" className="tnum" inputMode="numeric" value={draft.completed} onChange={(e) => update({ completed: e.target.value })} />
                    </Field>
                    <Field label={t('aw.field.targetAccepted')} htmlFor="aw-accepted">
                      <TextInput id="aw-accepted" className="tnum" inputMode="numeric" value={draft.accepted} onChange={(e) => update({ accepted: e.target.value })} />
                    </Field>
                  </FormGrid>
                  <FormGrid>
                    <Field label={t('aw.field.from')} htmlFor="aw-from" hint={timeZone ?? 'UTC'}>
                      <TextInput id="aw-from" className="tnum" type="datetime-local" value={draft.from} onChange={(e) => update({ from: e.target.value })} />
                    </Field>
                    <Field label={t('aw.field.to')} htmlFor="aw-to" hint={timeZone ?? 'UTC'}>
                      <TextInput id="aw-to" className="tnum" type="datetime-local" value={draft.to} onChange={(e) => update({ to: e.target.value })} />
                    </Field>
                  </FormGrid>
                  <Field label={t('aw.field.group')} htmlFor="aw-group">
                    <TextInput id="aw-group" className="tnum" value={draft.groupId} onChange={(e) => update({ groupId: e.target.value })} />
                  </Field>
                  <FormGrid>
                    <MoneyInput id="aw-per-driver" label={t('aw.field.perDriver')} currency={currency} locale={lang} valueMinor={draft.perDriverMinor} onChangeMinor={(v) => update({ perDriverMinor: v })} issueText={(i) => v('format.money_input_issue', i)} />
                    <MoneyInput id="aw-budget" label={t('aw.field.budget')} currency={currency} locale={lang} valueMinor={draft.budgetMinor} onChangeMinor={(v) => update({ budgetMinor: v })} issueText={(i) => v('format.money_input_issue', i)} />
                  </FormGrid>
                  <div>
                    <Button variant="primary" disabled={busy || draft.name.trim() === '' || draft.completed.trim() === '' || draft.from.trim() === '' || draft.to.trim() === '' || draft.perDriverMinor === undefined || draft.budgetMinor === undefined || (draft.prizeType === 'FIXED' ? draft.amountMinor === undefined : draft.bps.trim() === '')} onClick={() => void propose()}>{t('aw.action.propose')}</Button>
                  </div>
                </Measure>
              )}
            </Panel>
          )}

          <SectionTitle>{t('aw.section.list')}</SectionTitle>
          <ListTable
            columns={columns}
            rows={campaigns ?? []}
            rowKey={(c) => c.campaignId}
            onRowClick={(c) => void openGrants(c)}
            {...(paid !== undefined ? { selectedKey: paid.campaign.campaignId } : {})}
            actions={rowActions}
            actionsWidth="minmax(300px, max-content)"
            loading={campaigns === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('aw.loadFailed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('aw.empty')} />}
            minWidth={1200}
          />

          <Drawer open={paid !== undefined} onClose={() => setPaid(undefined)} labelledBy="aw-detail-title">
            {paid !== undefined && (
              <>
                <DrawerHeader titleId="aw-detail-title" title={paid.campaign.name} subtitle={<>{t('aw.section.grants')} · <IdRef value={paid.campaign.campaignId} /></>} onClose={() => setPaid(undefined)} closeLabel={t('ui.close')} />
                <DrawerBody>
                  <ListTable
                    columns={[
                      { key: 'driver', header: t('aw.col.driver'), width: 'minmax(90px, 1fr)', render: (g: AwardGrantView) => <PersonName id={g.driverId} /> },
                      { key: 'amount', header: t('aw.col.amount'), width: 'minmax(80px, 1fr)', numeric: true, render: (g) => <span className="tnum">{money(g.amount)}</span> },
                      { key: 'rides', header: t('aw.col.rides'), width: 'minmax(90px, 1fr)', render: (g) => <span className="tnum">{g.completedCount} / {g.acceptedCount}</span> },
                      { key: 'when', header: t('aw.col.when'), width: 'minmax(120px, 1fr)', render: (g) => <Text role="caption" tone="secondary" mono>{formatMarketInstant(g.grantedAt, timeZone, lang)}</Text> },
                    ]}
                    rows={paid.grants}
                    rowKey={(g) => g.grantId}
                    empty={<EmptyState title={t('aw.noGrants')} />}
                    minWidth={420}
                  />
                </DrawerBody>
                <DrawerFooter>
                  <Button variant="ghost" onClick={() => setPaid(undefined)}>{t('ui.close')}</Button>
                </DrawerFooter>
              </>
            )}
          </Drawer>
        </>
      )}
    </>
  );
}
